import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface TriageSelection {
  selected: ReadonlySet<string>
  isSelected(key: string): boolean
  toggle(key: string, range: boolean): void
  setAll(on: boolean): void
  clear(): void
  allState: boolean | 'indeterminate'
}

/**
 * SA-1329 (spec §4.1): which triage messages are ticked. `selected` is always
 * the ticks intersected with `keys`, so a message that left the list drops out;
 * a new `resetToken` (another thread, another tab) clears. `toggle(key, true)`
 * ticks everything between the last toggled key and this one, in `keys` order.
 *
 * A caller's checkbox toggles through `onClick={(e) => { e.preventDefault();
 * toggle(key, e.shiftKey) }}` with a no-op `onCheckedChange`: Radix skips its
 * own toggle on a prevented click, and Space on the focused box fires the same
 * click, so the shift key reaches the hook either way.
 */
export function useTriageSelection(keys: readonly string[], resetToken: string): TriageSelection {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set())
  const [token, setToken] = useState(resetToken)
  const anchor = useRef<string | null>(null)

  // A new token clears during render, so no render pairs the old ticks with the new list.
  if (token !== resetToken) {
    setToken(resetToken)
    setTicked(new Set())
  }
  useEffect(() => {
    anchor.current = null
  }, [resetToken])

  const selected = useMemo(() => {
    const out = new Set<string>()
    for (const key of keys) if (ticked.has(key)) out.add(key)
    return out
  }, [keys, ticked])

  const toggle = useCallback((key: string, range: boolean) => {
    // Read the anchor now: the updater runs later, after it has moved to this key.
    const from = range && anchor.current !== null ? keys.indexOf(anchor.current) : -1
    setTicked((prev) => {
      const next = new Set(prev)
      const to = keys.indexOf(key)
      if (from >= 0 && to >= 0) {
        const [lo, hi] = from < to ? [from, to] : [to, from]
        for (let i = lo; i <= hi; i += 1) next.add(keys[i])
      } else if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    anchor.current = key
  }, [keys])

  const setAll = useCallback((on: boolean) => {
    setTicked(on ? new Set(keys) : new Set())
    anchor.current = null
  }, [keys])
  const clear = useCallback(() => {
    setTicked(new Set())
    anchor.current = null
  }, [])
  const isSelected = useCallback((key: string) => selected.has(key), [selected])

  const allState: boolean | 'indeterminate' =
    selected.size === 0 ? false : selected.size === keys.length ? true : 'indeterminate'

  return { selected, isSelected, toggle, setAll, clear, allState }
}
